export default class Color {
  /**
   * The yiq lightness value that determines when the lightness of color changes from "dark" to "light".
   * Acceptable values are between 0 and 255.
   * @return {int}
   */
  static get yiqContrastThreshold() {
    return 150;
  }

  /**
   * The hex code to use for a dark contrast to a light color.
   * @return {string}
   */
  static get yiqDark() {
    return "#222222";
  }

  /**
   * The hex code to use for a light contrast to a dark color.
   * @return {string}
   */
  static get yiqLight() {
    return "#FFFFFF";
  }

  /**
   * Determine if the provided string is a valid hex code.
   * @param {string} str
   * @return {boolean}
   */
  static isHex(str) {
    return str.search(/[a-f\d]{2}/gi) > -1;
  }

  /**
   * Determine if the provided string is a valid hex code.
   * @param {string} str
   * @return {boolean}
   */
  static isFullHex(str) {
    return str.search(/[a-f\d]{6}/gi) > -1;
  }

  /**
   * Fetch value of the red channel of the hex code provided.
   * @param {string} hex
   * @return {int}
   */
  static red(hex) {
    var val = hex.substr(0, 2);
    if (!this.isHex(val)) {
      return 0;
    }
    return parseInt(val, 16);
  }

  /**
   * Fetch value of the green channel of the hex code provided.
   * @param {string} hex
   * @return {int}
   */
  static green(hex) {
    var val = hex.substr(2, 2);
    if (!this.isHex(val)) {
      return 0;
    }
    return parseInt(val, 16);
  }

  /**
   * Fetch value of the blue channel of the hex code provided.
   * @param {string} hex
   * @return {int}
   */
  static blue(hex) {
    var val = hex.substr(4, 2);
    if (!this.isHex(val)) {
      return 0;
    }
    return parseInt(val, 16);
  }

  /**
   * Convert the RGB channel value provided to a hex code.
   * @param {int} val
   * @return {string}
   */
  static int2hex(val) {
    val = Number.isInteger(val) ? val : parseInt(val, 10);
    if (isNaN(val) || val < 0) {
      return "00";
    } else if (val > 255) {
      return "FF";
    }
    var hex = val.toString(16);
    return hex.length === 1 ? `0${hex}` : `${hex}`;
  }

  /**
   * Conver the RGB channel values provided to a hex code.
   * @param {int} red
   * @param {int} green
   * @param {int} blue
   * @return {string}
   */
  static rgb2hex(red, green, blue) {
    return this.int2hex(red) + this.int2hex(green) + this.int2hex(blue);
  }

  /**
   * Determine the appropriate hex code to use that contrasts the hex code provided.
   * @param {string} hex
   * @return {string}
   */
  static yiq(hex) {
    var red = this.red(hex);
    var green = this.green(hex);
    var blue = this.blue(hex);

    var yiq = (red * 299 + green * 587 + blue * 114) / 1000;
    return yiq >= this.yiqContrastThreshold ? this.yiqDark : this.yiqLight;
  }
}
